import React, { useEffect, useContext, useState } from 'react';
import { AppContext, ModalContext, AjaxContext, formatListData, UserContext } from 'commons-ui';
import _isNaN from 'lodash/isNaN';
import _isNil from 'lodash/isNil';
import _find from 'lodash/find';
import { PAGE_NAMES, MISCELLANEOUS_RESOURCES, ACTION } from 'constant';
import { SelectFilterContent, DownloadRefundCsvContent } from '../submodules/selectFilter';
import { TRefundRequestFilters, IApproveAndRefundRequestInBulk, PAGE_LIMIT, TDownloadRefundsFilter } from '../constants';

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);
	const { setModalConfig } = useContext(ModalContext);
	const { user, permissionsConfig } = useContext(UserContext);
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [refundRequestsData, setRefundRequestsData] = useState<any>({ data: [], rowCount: 0 });
	const [pageNumber, setPageNumber] = useState<any>({ currPage: 1, nextPage: null });
	const [filters, setFilters] = useState<TRefundRequestFilters | undefined>(undefined);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);

	const handleFilterFormClose = () => {
		setModalConfig({ isOpen: false });
	};

	const fetchAllRefundRequestData = async (jumpPageNumber: number) => {
		try {
			setShowLoader(true);
			const requestBody: any = {
				approvalStatusArray: [filters?.requestStatus],
				...(!_isNaN(filters?.startTime) && !_isNaN(filters?.endTime)
					? { startTime: filters?.startTime, endTime: filters?.endTime }
					: {}),
				pageId: jumpPageNumber,
				limit: PAGE_LIMIT
			};
			const permission = _find(permissionsConfig, { actionName: ACTION.getTournamentRefundRequestInBulk });
			const response: any = await postData({
				endpoint: `/dashboard/getTournamentRefundRequestInBulk/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.refundRequest}-getTournamentRefundRequestInBulk`,
				queryParams: {
					appName
				},
				body: requestBody,
				customHeaders: {
					isMasked: permission?.isMasked || false
				}
			});
			setRefundRequestsData({ rowCount: response.data?.data.length, data: formatListData(response?.data?.data, 'id') });
			const recordsCount: number = response.data?.data.length;
			setPageNumber({ currPage: jumpPageNumber, nextPage: recordsCount !== PAGE_LIMIT ? null : jumpPageNumber + 1 });
			setForceUpdate(true);
		} catch (error: any) {
			setRefundRequestsData({ data: [], rowCount: 0 });
			setPageNumber({ currPage: ``, nextPage: null });
			setForceUpdate(true);
			showAlert('Error while getting refund requests data!', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const downloadRefundedRequestData = async (downloadFilters: TDownloadRefundsFilter) => {
		try {
			setShowLoader(true);
			const requestBody: any = {
				approvalStatusArray: ['approved', 'rejected'],
				startTime: downloadFilters?.startTime,
				endTime: downloadFilters?.endTime,
				pageId: 1,
				limit: 1000
			};
			const permission = _find(permissionsConfig, { actionName: ACTION.getTournamentRefundRequestInBulk });
			const response: any = await postData({
				endpoint: `/dashboard/getTournamentRefundRequestInBulk/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.refundRequest}-getTournamentRefundRequestInBulk`,
				queryParams: {
					appName
				},
				body: requestBody,
				customHeaders: {
					isMasked: permission?.isMasked || false
				}
			});
			const data = response.data?.data;
			const allPossibleKeys = [
				'zupeeUserId',
				'gameName',
				'tournamentId',
				'transactionId',
				'refundReason',
				'tournamentStartTime',
				'entryFee',
				'freshdeskTicketId',
				'requesterEmailId',
				'refundSuggestionResult',
				'refundSuggestionReason',
				'jiraTicketId', // Optional field
				'approvalStatus',
				'refundStatus',
				'requestCreatedAt',
				'requestUpdatedAt',
				'supervisedBy',
				'refundProcessTimestamp'
			];

			const normalizedData = data.map((item) => {
				const normalizedItem = { ...item };
				allPossibleKeys.forEach((key) => {
					if (!(key in normalizedItem)) {
						switch (key) {
							case 'jiraTicketId':
								normalizedItem[key] = 'N/A';
								break;
							default:
								normalizedItem[key] = '';
						}
					}
				});
				return normalizedItem;
			});

			const filteredData = normalizedData;
			const headers = allPossibleKeys.join(',');
			const rows = filteredData.map((item) => {
				return allPossibleKeys
					.map((key) => {
						let value = item[key];
						if (['tournamentStartTime', 'requestCreatedAt', 'requestUpdatedAt', 'refundProcessTimestamp'].includes(key)) {
							value = new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
						}
						if (value === null || value === undefined) {
							return '""';
						}
						return `"${String(value).replace(/"/g, '""')}"`;
					})
					.join(',');
			});

			const csvContent = [headers, ...rows].join('\n');
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			const timestamp = new Date().toISOString().split('T')[0];
			link.setAttribute('download', `Refunded-requests-data_${timestamp}.csv`);
			link.href = url;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			showAlert('Error while downloading refunded requests data!', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const approveAndRefundRequestInBulk = async (reqParams: IApproveAndRefundRequestInBulk, isRefunded: boolean) => {
		try {
			if (isRefunded) {
				showAlert('Refunds have already been processed for a part of the selection, Please check the entries selected.', {
					type: 'error'
				});
				return;
			}
			setShowLoader(true);
			const requestBody: any = {
				...reqParams,
				supervisedBy: user?.email
			};

			const response: any = await postData({
				endpoint: `/dashboard/approveAndRefundTournamentInBulk/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.refundRequest}-approveAndRefundTournamentInBulk`,
				queryParams: {
					appName
				},
				body: requestBody
			});

			if (response.data.success) {
				showAlert('Refund request executed successfuly!');
			} else {
				showAlert('Failed Refund request execution!');
			}
		} catch (error: any) {
			showAlert(error.message || 'Failed to execute this action.', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const handleFilterSubmit = (submittedFilters: TRefundRequestFilters) => {
		const { requestStatus, startTime, endTime } = submittedFilters;
		setFilters({
			requestStatus,
			...(!_isNil(startTime) && !_isNaN(startTime) ? { startTime } : {}),
			...(!_isNil(endTime) && !_isNaN(endTime) ? { endTime } : {})
		});
	};

	const handleRefundFilterSubmit = (submittedFilters: TDownloadRefundsFilter) => {
		const { startTime, endTime } = submittedFilters;
		downloadRefundedRequestData({
			...(!_isNil(startTime) && !_isNaN(startTime) ? { startTime } : {}),
			...(!_isNil(endTime) && !_isNaN(endTime) ? { endTime } : {})
		});
	};

	const handleFilterModalOpen = () => {
		setModalConfig({
			isOpen: true,
			header: 'Refund Request Filter',
			content: <SelectFilterContent handleFilterSubmit={handleFilterSubmit} onClose={handleFilterFormClose} />
		});
	};

	const handleRefundsDownloadModalOpen = () => {
		setModalConfig({
			isOpen: true,
			header: 'Download Refunds CSV Filter',
			content: <DownloadRefundCsvContent handleFilterSubmit={handleRefundFilterSubmit} onClose={handleFilterFormClose} />
		});
	};

	const getAllRefundRequestData = () => {
		setForceUpdate(false);
		return refundRequestsData;
	};

	const jumpBack = async () => {
		if (pageNumber.currPage > 1) {
			await fetchAllRefundRequestData(pageNumber.currPage - 1);
		}
	};

	const jumpNext = async () => {
		if (pageNumber.nextPage !== null) {
			await fetchAllRefundRequestData(pageNumber.nextPage);
		}
	};

	useEffect(() => {
		handleFilterModalOpen();
	}, []);

	useEffect(() => {
		if (filters !== null && filters !== undefined) {
			fetchAllRefundRequestData(1);
		}
	}, [filters]);

	return {
		getAllRefundRequestData,
		approveAndRefundRequestInBulk,
		showLoader,
		fetchAllRefundRequestData,
		handleFilterModalOpen,
		handleRefundsDownloadModalOpen,
		filters,
		forceUpdate,
		pageNumber,
		jumpBack,
		jumpNext,
		setShowLoader
	};
};

export default useService;
